import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Btn, Spinner } from "@/admin/components";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAdminAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [devCode, setDevCode] = useState("");
  const [emailFailed, setEmailFailed] = useState(false);
  const [provider, setProvider] = useState("");

  const sendCode = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/auth/request-otp", { email: email.trim().toLowerCase() });
      setDevCode(data.dev_code || "");
      setEmailFailed(!!data.email_failed);
      setProvider(data.provider || "");
      setStep("otp");
      toast.success(
        data.provider === "auth0"
          ? "Verification code sent to your email."
          : data.email_failed
          ? "Email delivery failed — use the code shown below."
          : data.dev_code
            ? "Use the test code shown below."
            : "Verification code sent to your email.",
      );
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/auth/verify-otp", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
      });
      login(data.token, data.email);
      navigate("/admin/overview", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-[var(--jk-paper)] px-6">
      <div className="w-full max-w-[380px]">
        <div className="mb-8 flex items-center gap-2">
          <img src="/logo.svg" alt="JothidaKural" className="h-[20px] w-auto" />
          <span className="rounded-full bg-[#111111] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
            Admin
          </span>
        </div>

        <div className="rounded-[20px] border border-[#e9e6e1] bg-white p-7">
          {step === "email" ? (
            <>
              <h1 className="text-[22px] font-semibold tracking-tight text-[#111]">Admin sign in</h1>
              <p className="mt-1.5 text-[14px] text-[#777]">Enter your admin email to receive a code.</p>
              <input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && email && sendCode()}
                placeholder="you@example.com"
                className="mt-6 h-12 w-full rounded-[12px] border border-[#e5e5e5] bg-[#f9f9f9] px-4 text-[14px] outline-none focus:border-[#111] focus:bg-white"
              />
              {error && <p className="mt-3 text-[13px] text-[#c0392b]" data-testid="admin-login-error">{error}</p>}
              <button
                data-testid="admin-send-code-btn"
                onClick={sendCode}
                disabled={!email || busy}
                className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-[14px] bg-[#111111] text-[15px] font-semibold text-white disabled:opacity-50"
              >
                {busy && <Spinner className="h-4 w-4 border-white/40 border-t-white" />}
                Send code
              </button>
            </>
          ) : (
            <>
              <h1 className="text-[22px] font-semibold tracking-tight text-[#111]">Enter code</h1>
              <p className="mt-1.5 text-[14px] text-[#777]">We sent a 6-digit code to {email}.</p>
              <input
                data-testid="admin-otp-input"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => e.key === "Enter" && code.length === 6 && verify()}
                placeholder="••••••"
                className="mt-6 h-12 w-full rounded-[12px] border border-[#e5e5e5] bg-[#f9f9f9] px-4 text-center text-[20px] tracking-[0.4em] outline-none focus:border-[#111] focus:bg-white"
              />
              {error && <p className="mt-3 text-[13px] text-[#c0392b]" data-testid="admin-login-error">{error}</p>}
              {devCode && provider !== "auth0" && (
                <div className="mt-4 rounded-[12px] bg-[var(--jk-butter)] px-4 py-3 text-[12px] text-[#6b5d2a]" data-testid="admin-dev-code-hint">
                  {emailFailed ? (
                    <>We couldn&apos;t deliver the email — your code is <span className="font-bold tracking-widest">{devCode}</span></>
                  ) : (
                    <>Dev mode — your code is <span className="font-bold tracking-widest">{devCode}</span> (email sending wires up with your Resend key)</>
                  )}
                </div>
              )}
              <button
                data-testid="admin-verify-btn"
                onClick={verify}
                disabled={code.length !== 6 || busy}
                className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-[14px] bg-[#111111] text-[15px] font-semibold text-white disabled:opacity-50"
              >
                {busy && <Spinner className="h-4 w-4 border-white/40 border-t-white" />}
                Verify & sign in
              </button>
              <button onClick={() => { setStep("email"); setCode(""); setError(""); setDevCode(""); setEmailFailed(false); setProvider(""); }} className="mt-4 w-full text-[13px] font-medium text-[#777] hover:text-[#111]">
                Use a different email
              </button>
            </>
          )}
        </div>
        <p className="mt-5 text-center text-[12px] text-[#999]">Private internal tool · Authorised access only</p>
        <button
          type="button"
          data-testid="admin-to-astrologer-signin"
          onClick={() => navigate("/signin")}
          className="mt-3 w-full text-center text-[13px] font-medium text-[#777] underline underline-offset-2 hover:text-[#111]"
        >
          Astrologer sign in
        </button>
      </div>
    </div>
  );
}
